import { Modal } from 'antd';
import Skeleton from 'react-loading-skeleton';
import {
  IconButton,
  NormalButton,
  AIECGViewer,
  ECGClient,
  CloseIcon as closeIcon,
} from '@/shared/ui';
import { timezoneToOffset } from '@/shared/lib';
import { convertToMomentJs } from '@/shared/lib/aiUtils';
import { STRIP_MANAGEMENT_TAB_ID } from '@/entities/report';
import useEditStripModal from './useEditStripModal';

const configChart = {
  isDisplayScrollbar: true,
  isEdit: true,
  hrInfo: {},
};

export type EditStripModalProps = {
  visible: boolean;
  isDeviceEvent?: boolean;
  data?: any;
  originalData?: any;
  updateEditData: (updateData: any) => void;
  ecgDataMap: any;
  timezoneOffset?: any;
  ecgViewerConfig?: any;
  handleClickCloseButton: () => void;
};

/**
 * Full-screen dialog for re-capturing an AI-captured event's ECG strip. useEditStripModal loads the ECG object and start epoch; the waveform area shows a skeleton while loading, a retry prompt when the download yielded no samples, and otherwise the AI ECG viewer configured for the strips management tab with AI assist, beat editing and fine-grid menus suppressed, while the footer cancels or saves the captured strip.
 *
 * @param props - Dialog props carrying the visible flag, the strip data, ECG data map, timezone offset, viewer config and the close handler.
 * @returns The rendered edit-strip dialog with its header, ECG viewer area and footer buttons.
 */
const EditStripModal = ({
  visible,
  isDeviceEvent,
  data,
  originalData,
  updateEditData,
  ecgDataMap,
  timezoneOffset,
  ecgViewerConfig,
  handleClickCloseButton,
}: EditStripModalProps) => {
  const {
    state,
    ecgObject,
    aiECGViewerRef,
    startStripEpoch,
    onClickSave,
    onClickCancel,
    handleCaptureImages,
    getCaptureMode,
    onSubmit,
    onClickRetry,
  } = useEditStripModal({
    visible,
    isDeviceEvent,
    data,
    originalData,
    updateEditData,
    ecgDataMap,
    timezoneOffset,
    ecgViewerConfig,
    handleClickCloseButton,
  });

  return (
    <Modal
      centered
      width={993}
      closable={false}
      className="edit-ai-strip-modal"
      wrapClassName="edit-ai-strip-modal-wrapper"
      footer={null}
      title={null}
      open={visible}
    >
      <div id="edit-strip">
        <div className="ai-strip-header">
          <div>EDIT STRIP</div>
          <IconButton
            isFilled
            iconComponent={<img src={closeIcon} alt="Close icon" />}
            onClick={handleClickCloseButton}
          />
        </div>
        <div className="ai-strip-content">
          <div className="start-time">
            <div>Start time:</div>
            <b>
              {data?.stripStart
                ? convertToMomentJs(data.stripStart, timezoneToOffset(data.timezone))?.format(
                    'MM/DD/YYYY, HH:mm:ss',
                  )
                : ''}
            </b>
          </div>
          <div className="ecg-strip">
            {state.isLoading ? (
              <div id="ecg-viewer" className="ecg-viewer-container">
                <div className="ecg-viewer-skeleton-container">
                  <div className="scroller-container">
                    <Skeleton height="100%" />
                  </div>
                  <div className="chart-container">
                    <Skeleton height="100%" />
                  </div>
                  <div className="menu-container">
                    <Skeleton height="100%" />
                  </div>
                </div>
              </div>
            ) : !ecgObject?.dataSamples?.length || !startStripEpoch.current ? (
              <div className="edit-strip-retry-space">
                <div className="retry-space-desc">
                  <div className="fallback-text">
                    An unexpected occurs: Failed to download ecg data <br />
                    Please retry!
                  </div>
                </div>
                <NormalButton
                  className="retry-space-btn"
                  buttonName="Retry"
                  onClick={onClickRetry}
                />
              </div>
            ) : (
              <ECGClient>
                <AIECGViewer
                  ref={aiECGViewerRef}
                  idComponents={{
                    canvasBackground: 'cvBgEventStripManagement',
                    exportGridCanvas: 'expGridCvEventStripManagement',
                    canvasChart: 'cvChartEventStripManagement',
                    canvasObject: 'cvObjEventStripManagement',
                    canvasScroller: 'cvScrollerEventStripManagement',
                    contextMenu: 'contextMenuEventStripManagement',
                    tooltip: 'tooltipEventStripManagement',
                    tab: STRIP_MANAGEMENT_TAB_ID,
                  }}
                  ecg={ecgObject}
                  ecgDataMap={ecgDataMap}
                  configChart={configChart}
                  isAppliedAI
                  isDisableAIAssist
                  isHideAddRemoveBeatMenu
                  isHideFineGridMenu
                  isHideMarkASampleMenu
                  hasArrowButtonScroller={false}
                  timeObject={{
                    startMoment: startStripEpoch.current,
                    timezoneOffset,
                  }}
                  aiChannel={state.aiChannel}
                  captureMode={getCaptureMode()}
                  handleCaptureImages={handleCaptureImages}
                  hideTechnicianComments
                  hideCaptureTitle
                  onSubmit={onSubmit}
                />
              </ECGClient>
            )}
          </div>
        </div>
        <div className="ai-strip-footer">
          <div className="left-part" />
          <div className="right-part">
            <NormalButton className="mr3" buttonName="Cancel" onClick={onClickCancel} />
            <NormalButton isFilled buttonName="Done" onClick={onClickSave} />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default EditStripModal;
